import React from 'react';
import { Dividend } from '../types';

interface DividendChartProps {
  data: Dividend[];
  period: 'month' | 'quarter' | 'year';
}

export const DividendChart: React.FC<DividendChartProps> = ({ data, period }) => {
  // Mock chart data - in a real app, this would come from actual dividend payment history
  const generateChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    
    return months.map((month, index) => {
      // Simulate dividend income based on the stocks in the portfolio
      let monthlyIncome = 0;
      
      data.forEach(dividend => {
        const frequencyMultiplier = {
          monthly: 1,
          quarterly: 0.33,
          semi_annual: 0.17,
          annual: 0.08
        };
        
        // Simulate payments throughout the year
        if (dividend.frequency === 'monthly') {
          monthlyIncome += dividend.total_amount;
        } else if (dividend.frequency === 'quarterly' && index % 3 === 0) {
          monthlyIncome += dividend.total_amount;
        } else if (dividend.frequency === 'semi_annual' && (index === 0 || index === 6)) {
          monthlyIncome += dividend.total_amount;
        } else if (dividend.frequency === 'annual' && index === 0) {
          monthlyIncome += dividend.total_amount;
        }
      });
      
      return {
        month,
        income: Math.round(monthlyIncome)
      };
    });
  };

  const chartData = generateChartData();
  const maxIncome = Math.max(...chartData.map(d => d.income));

  return (
    <div className="w-full h-full">
      <div className="flex items-end justify-between h-48 space-x-2">
        {chartData.map((item, index) => (
          <div key={index} className="flex-1 flex flex-col items-center">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-t relative group">
              <div
                className="bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600"
                style={{
                  height: `${(item.income / maxIncome) * 100}%`,
                  minHeight: item.income > 0 ? '4px' : '0'
                }}
              />
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                ${item.income.toLocaleString()}
              </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
              {item.month}
            </div>
          </div>
        ))}
      </div>
      
      {/* Summary stats */}
      <div className="mt-6 grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            ${chartData.reduce((sum, item) => sum + item.income, 0).toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total {period === 'year' ? 'Annual' : period === 'quarter' ? 'Quarterly' : 'Monthly'} Income</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            ${Math.round(chartData.reduce((sum, item) => sum + item.income, 0) / 12).toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Average Monthly</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {chartData.filter(item => item.income > 0).length}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Payment Months</div>
        </div>
      </div>
    </div>
  );
}; 